import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBuilder } from "@/context/BuilderContext";
import { chordMidis } from "@/lib/theory";
import { playProgression } from "@/lib/audio";
import { Heart, Play, Trash2, Trophy, Library, Bookmark } from "lucide-react";

function ProgCard({ p, onLike, onLoad, onDelete, mine, userId }) {
  const liked = !!userId && (p.likes || []).includes(userId);
  return (
    <div className="wtk-comm-card" data-testid="community-card">
      <div className="wtk-comm-top">
        <span className="wtk-comm-title">{p.title}</span>
        <span className="wtk-comm-key">{p.tonic} {p.mode}</span>
      </div>
      {p.note && <p className="wtk-comm-note">"{p.note}"</p>}
      <div className="wtk-comm-chords">{p.chords.map((c) => c.label).join(" · ")} · {p.bpm} BPM</div>
      <div className="wtk-comm-foot">
        <span className="wtk-comm-by">{mine ? "You" : p.user_name}</span>
        <div className="wtk-comm-actions">
          <button className="wtk-icon-btn" title="Play/Load" data-testid="load-progression" onClick={() => onLoad(p)}><Play size={15} /></button>
          {onLike && (
            <button className={`wtk-icon-btn ${liked ? "liked" : ""}`} title="Like" data-testid="like-btn" onClick={() => onLike(p)}>
              <Heart size={15} /> <span>{(p.likes || []).length}</span>
            </button>
          )}
          {mine && <button className="wtk-icon-btn" title="Delete" data-testid="delete-progression" onClick={() => onDelete(p)}><Trash2 size={15} /></button>}
        </div>
      </div>
    </div>
  );
}

export default function Community() {
  const { user, openAuth } = useAuth();
  const { loadProgression, setKey } = useBuilder();
  const [sort, setSort] = useState("newest");
  const [list, setList] = useState([]);
  const [mine, setMine] = useState([]);
  const [challenge, setChallenge] = useState(null);

  const refresh = useCallback(async () => {
    const [c, ch] = await Promise.all([
      api.get(`/community?sort=${sort}`),
      api.get(`/challenge`),
    ]);
    setList(c.data);
    setChallenge(ch.data);
    if (user) {
      try { const m = await api.get(`/progressions/mine`); setMine(m.data); } catch (e) { /* ignore */ }
    } else {
      setMine([]);
    }
  }, [sort, user]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("wtk-progressions-updated", h);
    return () => window.removeEventListener("wtk-progressions-updated", h);
  }, [refresh]);

  const play = (p) => {
    playProgression(p.chords.map((c) => chordMidis(c.root, c.quality)), p.bpm || 100, (p.bars || 1) * 4, false);
    loadProgression(p);
  };
  const like = async (p) => {
    if (!user) { openAuth("login"); return; }
    await api.post(`/progressions/${p.id}/like`);
    refresh();
  };
  const del = async (p) => {
    await api.delete(`/progressions/${p.id}`);
    refresh();
  };

  return (
    <section className="wtk-community" id="community" data-testid="community">
      <div className="wtk-community-inner">
        {challenge && (
          <div className="wtk-challenge" data-testid="weekly-challenge">
            <div className="wtk-eyebrow"><Trophy size={15} /> Weekly challenge · {challenge.week}</div>
            <h2 className="wtk-section-h">This week, write something in <span className="wtk-title-accent">{challenge.key}</span></h2>
            <button className="wtk-analyze-btn" style={{ width: "auto" }} data-testid="build-challenge"
              onClick={() => setKey(challenge.tonic, challenge.mode)}>
              Build in {challenge.key}
            </button>
          </div>
        )}

        {user && (
          <div className="wtk-comm-block" data-testid="saved-progressions">
            <h3 className="wtk-section-h"><Bookmark size={17} /> Your saved progressions</h3>
            {mine.length === 0 ? (
              <p className="wtk-empty">Nothing saved yet — build a progression and hit Save.</p>
            ) : (
              <div className="wtk-comm-grid">
                {mine.map((p) => <ProgCard key={p.id} p={p} mine onLoad={play} onDelete={del} />)}
              </div>
            )}
          </div>
        )}

        <div className="wtk-comm-block">
          <div className="wtk-comm-head">
            <h3 className="wtk-section-h"><Library size={17} /> Community library</h3>
            <div className="wtk-seg">
              <button className={sort === "newest" ? "on" : ""} data-testid="sort-newest" onClick={() => setSort("newest")}>Newest</button>
              <button className={sort === "loved" ? "on" : ""} data-testid="sort-loved" onClick={() => setSort("loved")}>Most loved</button>
            </div>
          </div>
          {list.length === 0 ? (
            <p className="wtk-empty">No public progressions yet — be the first to share one!</p>
          ) : (
            <div className="wtk-comm-grid" data-testid="community-list">
              {list.map((p) => <ProgCard key={p.id} p={p} userId={user?.id} onLoad={play} onLike={like} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
